import { useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import * as XLSX from 'xlsx';
import { CalendarClock, Download, FolderKanban, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  EYEBROW_CLASS,
  OMS_COLORS,
  RESOURCE_STATUS_SEGMENTS,
  baseChartOptions,
} from '../omsDashTheme';
import { dashboardService } from '../../../../services/dashboardService';
import type { OmsResourceRow } from '../types';
import type { EngineerPerf } from '../utils/performanceModel';
import type { ScopeWindow } from '../utils/scopeWindow';
import { windowLabel } from '../utils/scopeWindow';
import { formatNumber } from '../utils/formatNumber';
import { StatusBar, StatusLegendChips, statusCountsOf, totalOfStatusCounts as totalOf } from './StatusBits';
import { BandChip, DeltaChip, ScoreDial, ScoreExplainer } from './HealthScore';
import StatCount, { type StatCountProps } from './StatCount';
import { EngineerStateChip } from './EngineerLeaderboard';
import ActivityHeatmap from './ActivityHeatmap';

interface Props {
  /** Scored engineer node for the current scope (null = closed). */
  engineer: EngineerPerf | null;
  /** Raw windowed row — carries the per-project segments. */
  row: OmsResourceRow | null;
  window: ScopeWindow;
  deltaPhrase: string;
  onClose: () => void;
}

function StatTile(props: Omit<StatCountProps, 'size'>) {
  return (
    <div className="rounded-xl border border-[rgba(11,59,46,0.18)] px-3 py-2" style={{ backgroundColor: '#F8F2E2' }}>
      <StatCount {...props} size="md" />
    </div>
  );
}

/** Engineer report — the drawer's per-project table as a one-sheet XLSX. */
function exportEngineerReport(engineer: EngineerPerf, row: OmsResourceRow, window: ScopeWindow) {
  const rows = row.projects.map((proj) => ({
    Project: proj.projectName,
    Completed: proj.completedTasks,
    Pass: proj.statusCounts.pass,
    Fail: proj.statusCounts.fail,
    'In progress': proj.statusCounts.inprogress,
    Due: proj.statusCounts.due,
    Overdue: proj.statusCounts.overdue,
    'On hold': proj.statusCounts.hold,
    Engineer: engineer.name,
    'Health score': engineer.score?.score ?? '—',
    From: window.from,
    To: window.to,
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Engineer report');
  const slug = engineer.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  XLSX.writeFile(wb, `sorigin-performance_${slug}_${window.from}_${window.to}.xlsx`);
  toast.success(`Report downloaded — ${engineer.name} · ${windowLabel(window)}`);
}

/**
 * Engineer detail drawer (Explorer Level 3 → person). Score dial with the
 * "how it's computed" explainer, the activity heatmap for the selected
 * window, status donut and per-project workload split.
 */
export default function EngineerDetailDrawer({ engineer, row, window, deltaPhrase, onClose }: Props) {
  useEffect(() => {
    if (!engineer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [engineer, onClose]);

  const activityQuery = useQuery({
    queryKey: ['oms-engineer-activity', engineer?.id, window.from, window.to],
    queryFn: () => dashboardService.getOmsEngineerActivity(engineer!.id, window),
    enabled: engineer !== null,
    staleTime: 5 * 60 * 1000,
  });

  const donut = useMemo(() => {
    if (!engineer) return null;
    const counts = engineer.counts;
    const active = RESOURCE_STATUS_SEGMENTS.filter((seg) => counts[seg.key] > 0);
    if (active.length === 0) return null;
    const options: ApexOptions = {
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, type: 'donut' },
      labels: active.map((s) => s.label),
      colors: active.map((s) => s.color),
      stroke: { width: 2, colors: ['#FAF7EE'] },
      legend: { ...baseChartOptions.legend, position: 'bottom' },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              name: { show: true, fontSize: '10px', color: OMS_COLORS.caption },
              value: { show: true, fontSize: '22px', fontWeight: 800, color: OMS_COLORS.tealDark, formatter: (v: string) => v },
              total: {
                show: true,
                label: 'Tasks',
                fontSize: '10px',
                color: OMS_COLORS.caption,
                formatter: () => formatNumber(totalOf(counts)),
              },
            },
          },
        },
      },
      tooltip: { theme: 'light', y: { formatter: (v: number) => `${v} task${v === 1 ? '' : 's'}` } },
    };
    return { options, series: active.map((s) => counts[s.key]) };
  }, [engineer]);

  return (
    <AnimatePresence>
      {engineer && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-[rgba(42,31,23,0.35)]"
            aria-hidden
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={`Performance detail for ${engineer.name}`}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-[rgba(11,59,46,0.28)] shadow-2xl"
            style={{ backgroundColor: '#F2EADB' }}
          >
            {/* Header */}
            <div className="border-b border-[rgba(11,59,46,0.18)] px-5 py-4" style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <ScoreDial score={engineer.score} size={62} label={`Health score for ${engineer.name}`} />
                  <div className="min-w-0">
                    <p className={EYEBROW_CLASS}>Site engineer · performance</p>
                    <h2 className="mt-0.5 truncate text-xl font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                      {engineer.name}
                    </h2>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                      <span>{engineer.roleName || 'Site role'}</span>
                      <EngineerStateChip state={engineer.state} />
                      <span className="text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.caption }}>
                        Health score
                      </span>
                      <BandChip score={engineer.score} />
                      <DeltaChip delta={engineer.delta} phrase={`pts ${deltaPhrase}`} />
                      {engineer.score && <ScoreExplainer score={engineer.score} />}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close engineer detail"
                  className="rounded-lg p-1.5 transition hover:bg-[rgba(11,59,46,0.08)]"
                  style={{ color: OMS_COLORS.tealDark }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: OMS_COLORS.champagneDeep }}>
                  <CalendarClock className="h-3 w-3" />
                  {windowLabel(window)}
                </p>
                {row && (
                  <button
                    type="button"
                    onClick={() => exportEngineerReport(engineer, row, window)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgba(11,59,46,0.24)] px-2.5 py-1.5 text-[10px] font-extrabold uppercase tracking-wide transition hover:bg-[rgba(11,59,46,0.05)]"
                    style={{ color: '#1F5A47', backgroundColor: '#fcfaf3' }}
                  >
                    <Download className="h-3 w-3" /> Export engineer report
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="sovereign-scrollbar flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatTile label="Scheduled" value={engineer.scheduled} noun="tasks" ink={OMS_COLORS.tealDark} />
                <StatTile label="Completed" value={engineer.completed} of={engineer.scheduled} noun="tasks" ink={OMS_COLORS.teal} />
                <StatTile
                  label="Open"
                  value={engineer.open}
                  noun="tasks"
                  qualifier="open"
                  ink={engineer.state === 'overloaded' ? OMS_COLORS.red : OMS_COLORS.champagneDeep}
                />
                <StatTile
                  label="Overdue"
                  value={engineer.overdue}
                  of={engineer.open}
                  noun="open tasks"
                  ink={engineer.overdue > 0 ? OMS_COLORS.red : OMS_COLORS.tealDark}
                />
              </div>
              {engineer.projectNames.length > 0 && (
                <p className="px-0.5 text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                  Score and totals cover {engineer.projectNames.length === 1 ? engineer.projectNames[0] : `${engineer.projectNames.length} projects in scope`} within the selected window.
                </p>
              )}

              {/* Activity heatmap */}
              <section
                className="rounded-2xl border border-[rgba(11,59,46,0.22)] p-4"
                style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}
                aria-label="Submission activity"
              >
                <p className={EYEBROW_CLASS}>Submission activity</p>
                <div className="mt-2">
                  {activityQuery.isLoading ? (
                    <div className="h-28 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
                  ) : activityQuery.isError ? (
                    <p className="py-4 text-sm" style={{ color: OMS_COLORS.caption }}>
                      Activity feed unavailable — refresh to retry.
                    </p>
                  ) : (
                    <ActivityHeatmap days={activityQuery.data?.days ?? []} window={window} name={engineer.name} />
                  )}
                </div>
              </section>

              {/* Status distribution */}
              <section
                className="rounded-2xl border border-[rgba(11,59,46,0.22)] p-4"
                style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}
                aria-label="Status distribution"
              >
                <p className={EYEBROW_CLASS}>Status distribution</p>
                {donut ? (
                  <Chart type="donut" height={240} options={donut.options} series={donut.series} />
                ) : (
                  <p className="py-8 text-center text-sm" style={{ color: OMS_COLORS.caption }}>
                    No tasks in this window.
                  </p>
                )}
                <div className="mt-2">
                  <StatusLegendChips counts={engineer.counts} />
                </div>
              </section>

              {/* Per-project breakdown */}
              {row && (
                <section
                  className="rounded-2xl border border-[rgba(11,59,46,0.22)] p-4"
                  style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}
                  aria-label="Per-project breakdown"
                >
                  <div className="flex items-center gap-1.5">
                    <FolderKanban className="h-3.5 w-3.5" style={{ color: OMS_COLORS.champagneDeep }} />
                    <p className={EYEBROW_CLASS}>
                      All assignments in window · {row.projects.length} project{row.projects.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <ul className="mt-3 space-y-3">
                    {row.projects.map((proj) => {
                      const projCounts = statusCountsOf(proj);
                      const projTotal = totalOf(projCounts);
                      const donePct = projTotal > 0 ? Math.round(((projCounts.pass + projCounts.fail) / projTotal) * 100) : 0;
                      return (
                        <li key={proj.projectId}>
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="min-w-0 truncate text-[13px] font-bold" style={{ color: OMS_COLORS.ink }}>
                              {proj.projectName}
                            </p>
                            <p className="shrink-0 text-[10px] font-semibold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                              {donePct}% complete of {formatNumber(projTotal)} tasks
                            </p>
                          </div>
                          <div className="mt-1.5">
                            <StatusBar counts={projCounts} height={8} />
                          </div>
                          <div className="mt-1.5">
                            <StatusLegendChips counts={projCounts} />
                          </div>
                        </li>
                      );
                    })}
                    {row.projects.length === 0 && (
                      <li className="py-4 text-center text-sm" style={{ color: OMS_COLORS.caption }}>
                        No project assignments in this window.
                      </li>
                    )}
                  </ul>
                </section>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
